import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from "@/context/AuthContext";
import { useAiPageContext } from '@/context/AiPageContextProvider';
import { contextHelpers } from '@/components/AiCommandPalette';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input"; // Add this import
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Scatter, ReferenceLine } from 'recharts';
import { motion } from "framer-motion";
import { FiClock, FiActivity, FiSettings, FiCalendar, FiExternalLink } from 'react-icons/fi';
import { AiOutlineLoading3Quarters, AiOutlineEdit, AiOutlineReload } from "react-icons/ai";
import { GoArrowLeft } from "react-icons/go";
import { MdOutlineEdit } from "react-icons/md";
import { MdOutlineMoreVert, MdOutlinePause } from "react-icons/md";
import { FaPause, FaPlay } from "react-icons/fa6";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from "@/components/ui/dialog";
import {
    Tooltip as TooltipUI,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import colors from 'tailwindcss/colors';
import { RunDetailsDialog } from '@/components/RunDetailsDialog';
import { cn } from "@/lib/utils";

const formatResponseTime = (ms, longFormat = false) => {
    if (!ms && ms !== 0) return 'N/A';
    
    const formatDecimal = (num) => {
        // Convert to fixed first to handle floating point precision
        const fixed = Number(num.toFixed(1));
        // Check if it's effectively a whole number
        return fixed === Math.floor(fixed) ? Math.floor(fixed) : fixed;
    };

    if (ms < 1000) {
        return `${Math.round(ms)}${longFormat ? ' milliseconds' : ' ms'}`;
    } else if (ms < 60000) {
        const seconds = ms / 1000;
        return `${formatDecimal(seconds)}${longFormat ? ' seconds' : ' s'}`;
    } else {
        const minutes = ms / 60000;
        return `${formatDecimal(minutes)}${longFormat ? ' minutes' : ' min'}`;
    }
};

// TODO: Move this to a shared component
const MinimalBadge = ({ children, className }) => (
    <div className={cn(
        "rounded-sm border border-violet-200/50 bg-violet-50/50 px-2 py-0.5 text-[10px] font-medium uppercase text-violet-700 dark:border-violet-800/50 dark:bg-violet-950/50 dark:text-violet-300",
        className
    )}>
        {children}
    </div>
);

const CustomYAxisTick = ({ x, y, payload }) => {
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={4}
                textAnchor="end"
                fill="hsl(var(--muted-foreground))"
                fontSize={12}
            >
                {`${payload.value} ms`}
            </text>
        </g>
    );
};

const TooltipItem = ({ label, value }) => (
    <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{label}:</span>
        <span className="font-medium ml-2">{value}</span>
    </div>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="bg-background p-4 rounded-lg shadow-lg border border-border">
                <p className="font-bold text-lg mb-2 text-primary">{new Date(data.timestamp).toLocaleString()}</p>
                {data.runCount ? (
                    <div className="space-y-2">
                        <TooltipItem label="Avg Response Time" value={formatResponseTime(data.responseTime)} />
                        <TooltipItem label="Success Rate" value={`${data.successRate.toFixed(2)}%`} />
                        <TooltipItem label="Run Count" value={data.runCount} />
                    </div>
                ) : (
                    <div className="space-y-2">
                        <TooltipItem label="Response Time" value={formatResponseTime(data.responseTime)} />
                        <TooltipItem label="Status" value={data.status} />
                    </div>
                )}
            </div>
        );
    }
    return null;
};

const InsightGrid = ({ insights }) => {
    const daysToShow = 14;
    const responseLevels = [
        'bg-violet-500/20',  // No data/slowest
        'bg-violet-500/60',  // Slow
        'bg-violet-500/80',  // Medium
        'bg-violet-500'   // Fast
    ];
    
    const getDateKey = (daysAgo) => {
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        return date.toISOString().split('T')[0];
    };

    const getPerformanceLevel = (avgResponseTime) => {
        if (!avgResponseTime) return 0;
        if (avgResponseTime > 1000) return 1;
        if (avgResponseTime > 500) return 2;
        return 3;
    };

    const insightMap = insights?.reduce((acc, day) => {
        acc[day.date] = day;
        return acc;
    }, {}) || {};

    const formatTooltipData = (dateKey, dayData) => {
        return (
            <div className="space-y-1.5">
                <p className="font-semibold">{dateKey}</p>
                <div className="text-sm space-y-1">
                    <p>Response Time: {formatResponseTime(dayData.avgResponseTime)}</p>
                    <p>Incidents: {dayData.incidents || 0}</p>
                    <p>Runs: {dayData.totalRuns || 0}</p>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-2">
            <div className="flex gap-1.5">
                <TooltipProvider>
                    {Array(daysToShow).fill().map((_, dayIndex) => {
                        const daysAgo = daysToShow - dayIndex - 1;
                        const dateKey = getDateKey(daysAgo);
                        const dayData = insightMap[dateKey] || { avgResponseTime: 0, incidents: 0, totalRuns: 0 };
                        const level = getPerformanceLevel(dayData.avgResponseTime);
                        
                        return (
                            <TooltipUI key={dayIndex} delayDuration={0}>
                                <TooltipTrigger asChild>
                                    <div
                                        className={`
                                            w-6 h-6 rounded-sm relative
                                            ${responseLevels[level]}
                                            transition-colors
                                            hover:ring-2 hover:ring-violet-200
                                            cursor-pointer
                                        `}
                                    >
                                        {dayData.incidents > 0 && (
                                            <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
                                        )}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {formatTooltipData(dateKey, dayData)}
                                </TooltipContent>
                            </TooltipUI>
                        );
                    })}
                </TooltipProvider>
            </div>
        </div>
    );
};

const ResponseTimeChart = ({ data }) => {
    const [activeDataPoint, setActiveDataPoint] = useState(null);
    const navigate = useNavigate();
    const { id } = useParams();
    
    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">No data available</p>
            </div>
        );
    }

    // Function to determine if a data point represents a failed status
    const isFailedStatus = (dataPoint) => {
        // Check for "Failure" status (with capital F)
        return dataPoint.status === "Failure";
    };

    // Get failed data points
    const failedDataPoints = data.filter(isFailedStatus);
    console.log("Failed data points:", failedDataPoints);

    // Custom active dot component that changes color based on status
    const CustomActiveDot = (props) => {
        const { cx, cy, payload } = props;
        const isFailed = isFailedStatus(payload);
        
        return (
            <circle 
                cx={cx} 
                cy={cy} 
                r={4} 
                fill={isFailed ? colors.red[500] : colors.violet[500]} 
                stroke={isFailed ? colors.red[500] : colors.violet[500]} 
                strokeWidth={2}
            />
        );
    };

    // Handle mouse move to update active data point
    const handleMouseMove = (e) => {
        if (e && e.activePayload && e.activePayload.length > 0) {
            setActiveDataPoint(e.activePayload[0].payload);
        } else {
            setActiveDataPoint(null);
        }
    };

    // Handle click on the chart
    const handleClick = (e) => {
        if (activeDataPoint && activeDataPoint.runId) {
            navigate(`/monitors/${id}/runs/${activeDataPoint.runId}`);
        }
    };

    // Handle key press (Enter key)
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && activeDataPoint && activeDataPoint.runId) {
            navigate(`/monitors/${id}/runs/${activeDataPoint.runId}`);
        }
    };

    // Determine cursor color based on active data point
    const cursorColor = activeDataPoint && isFailedStatus(activeDataPoint) 
        ? colors.red[500] 
        : "hsl(var(--border))";

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart 
                data={data}
                onMouseMove={handleMouseMove}
                onClick={handleClick}
                onKeyPress={handleKeyPress}
                tabIndex={0}
                role="button"
                aria-label="Response time chart - click on a data point to view details"
            >
                <CartesianGrid 
                    vertical={false} 
                    horizontal={true}
                    stroke="hsl(var(--border))"
                    strokeWidth={1}
                />
                <XAxis
                    dataKey="timestamp"
                    axisLine={false}
                    tickLine={false}
                    tick={false}
                />
                <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={<CustomYAxisTick />}
                />
                <RechartsTooltip 
                    content={<CustomTooltip />} 
                    cursor={{ 
                        stroke: cursorColor, 
                        strokeWidth: 2,
                        strokeDasharray: "0" // Solid line
                    }}
                />
                <Line 
                    type="monotone" 
                    dataKey="responseTime" 
                    stroke={colors.violet[500]}
                    strokeWidth={2} 
                    dot={false}
                    activeDot={<CustomActiveDot />}
                />
                {failedDataPoints.map((dataPoint, index) => (
                    <ReferenceLine
                        key={`ref-${index}`}
                        x={dataPoint.timestamp}
                        stroke={colors.red[500]}
                        strokeWidth={2}
                        strokeDasharray="3 3"
                    />
                ))}
                <Scatter
                    data={failedDataPoints}
                    dataKey="responseTime"
                    fill={colors.red[500]}
                    stroke={colors.red[500]}
                    strokeWidth={2}
                    shape="circle"
                    r={3}
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

const MonitorDetails = () => {
    const [monitor, setMonitor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
    const [newMonitorName, setNewMonitorName] = useState();
    const { id, runId } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [pauseToggleLoading, setPauseToggleLoading] = useState(false);
    const { axiosInstance } = useAuth();
    const { setCurrentAiPageContext } = useAiPageContext();

    const fetchMonitorDetails = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/monitors/${id}`);
            console.log("Monitor data:", response.data);
            console.log("Chart data:", response.data.chartData);
            const monitorData = response.data;
            setMonitor(monitorData);
            setNewMonitorName(monitorData.name);
            
            // Set page context for AI Command Palette
            const monitorContext = contextHelpers.createCustomContext(
                'monitor',
                monitorData.name,
                {
                    id,
                    name: monitorData.name,
                    url: monitorData.url?.formatted,
                    status: monitorData.lastRun?.success ? 'up' : 'down',
                    isActive: monitorData.isActive,
                    actionType: monitorData.actionType,
                    responseTime: monitorData.lastRun?.result?.responseTime,
                    uptime: monitorData.stats?.last24h?.uptime
                }
            );
            setCurrentAiPageContext(monitorContext);
        } catch (error) {
            console.error("Error fetching monitor details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMonitorDetails();
    }, [id]);

    const fadeInVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1 }
    };

    if (loading) {
        return <Layout><LoadingState /></Layout>;
    }

    if (!monitor) {
        return <Layout><div>Monitor not found</div></Layout>;
    }

    const handleEdit = () => {
        navigate(`/monitors/${id}/edit`);
    };

    const handleDelete = () => {
        setIsDeleteDialogOpen(true);
        setDeleteConfirmation('');
    };

    const confirmDelete = async () => {
        try {
            await axiosInstance.delete(`/monitors/${id}`);
            navigate('/monitors');
            toast({
                title: "The monitor has been deleted.",
                variant: "default",
            });
        } catch (error) {
            console.error("Error deleting monitor:", error);
            toast({
                title: "Failed to delete the monitor.",
                description: "Please try again later.",
                variant: "destructive",
            });
        } finally {
            setIsDeleteDialogOpen(false);
        }
    };

    const handleRunOnce = async () => {
        try {
            const response = await axiosInstance.post(`/monitors/${id}/run`);
            toast({
                title: "Monitor Run Initiated",
                description: "The monitor has been triggered to run once.",
            });
        } catch (error) {
            console.error("Error running monitor:", error);
            toast({
                title: "Failed to run monitor",
                description: "An error occurred while trying to run the monitor.",
                variant: "destructive",
            });
        }
    };

    const handleRename = async () => {
        try {
            await axiosInstance.put(`/monitors/${id}/rename`, { name: newMonitorName });
            const updatedMonitor = { ...monitor, name: newMonitorName };
            setMonitor(updatedMonitor);
            
            // Update context when monitor name changes
            const monitorContext = contextHelpers.createCustomContext(
                'monitor',
                newMonitorName,
                {
                    id,
                    name: newMonitorName,
                    url: updatedMonitor.url?.formatted,
                    status: updatedMonitor.lastRun?.success ? 'up' : 'down',
                    isActive: updatedMonitor.isActive,
                    actionType: updatedMonitor.actionType,
                    responseTime: updatedMonitor.lastRun?.result?.responseTime,
                    uptime: updatedMonitor.stats?.last24h?.uptime
                }
            );
            setCurrentAiPageContext(monitorContext);
            
            setIsRenameDialogOpen(false);
            toast({
                title: "Monitor renamed successfully",
                variant: "default",
            });
        } catch (error) {
            console.error("Error renaming monitor:", error);
            toast({
                title: "Failed to rename monitor",
                description: "Please try again later.",
                variant: "destructive",
            });
        }
    };

    const handlePauseToggle = async () => {
        setPauseToggleLoading(true);
        try {
            await axiosInstance.put(`/monitors/${id}/toggle-active`, { isActive: !monitor.isActive });
            const updatedMonitor = { ...monitor, isActive: !monitor.isActive };
            setMonitor(updatedMonitor);
            
            // Update context when monitor status changes
            const monitorContext = contextHelpers.createCustomContext(
                'monitor',
                updatedMonitor.name,
                {
                    id,
                    name: updatedMonitor.name,
                    url: updatedMonitor.url?.formatted,
                    status: updatedMonitor.lastRun?.success ? 'up' : 'down',
                    isActive: updatedMonitor.isActive,
                    actionType: updatedMonitor.actionType,
                    responseTime: updatedMonitor.lastRun?.result?.responseTime,
                    uptime: updatedMonitor.stats?.last24h?.uptime
                }
            );
            setCurrentAiPageContext(monitorContext);
            
            toast({
                title: monitor.isActive ? "Monitor paused" : "Monitor resumed",
                description: monitor.isActive ? "The monitor has been paused" : "The monitor is now active",
            });
        } catch (error) {
            console.error("Error toggling monitor state:", error);
            toast({
                title: "Failed to update monitor state",
                description: "Please try again later.",
                variant: "destructive",
            });
        } finally {
            setPauseToggleLoading(false);
        }
    };

    const StatSection = ({ title, value, description }) => (
        <div className="flex-1 px-4">
            <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
    );

    const handleRunClick = (run) => {
        navigate(`/monitors/${id}/runs/${run.id}`);
    };

    return (
        <Layout>
            <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5 }}
                className="space-y-6"
            >
                <div className="flex justify-between items-center">
                    <div className="flex-col items-center space-y-2">
                        <div className="flex items-center space-x-4">
                            <div 
                                className="flex items-center space-x-4 cursor-pointer text-3xl group"
                                onClick={() => navigate('/monitors')}
                            >
                                <GoArrowLeft className="group-hover:text-violet-500 transition-colors" />
                                <h1 className="-mt-0.5 font-semibold">{monitor.name}</h1>
                            </div>
                            <TooltipProvider>
                                <TooltipUI>
                                    <TooltipTrigger>
                                        <div className="relative mt-0.5">
                                            <div className={`w-4 h-4 ${monitor.isActive ? 'bg-emerald-400' : 'bg-yellow-500'} rounded-full`} />
                                            {monitor.isActive && (
                                                <div className="absolute inset-0 w-4 h-4 bg-emerald-400 rounded-full animate-ping" />
                                            )}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {monitor.isActive ? 'Monitor is active' : 'Monitor is paused'}
                                    </TooltipContent>
                                </TooltipUI>
                            </TooltipProvider>
                        </div>
                        <div className="flex items-center space-x-2 pl-12">
                            <a 
                                href={monitor.url.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-muted-foreground -mt-0.5 hover:text-primary flex items-center gap-1 transition-colors"
                            >
                                {monitor.url.formatted}
                                <FiExternalLink className="h-3 w-3" />
                            </a>
                            {monitor.actionType && (
                                <MinimalBadge>
                                    {monitor.actionType.replace('_', ' ')}
                                    {monitor.url.method && ` : ${monitor.url.method}`}
                                </MinimalBadge>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <Button variant="secondary" onClick={handlePauseToggle} disabled={pauseToggleLoading} loading={pauseToggleLoading}>
                            {monitor.isActive ? <FaPause className="mr-2 h-4 w-4" /> : <FaPlay className="mr-2 h-4 w-4" />}
                            {monitor.isActive ? 'Pause' : 'Resume'}
                        </Button>
                        <Button variant="secondary" onClick={handleEdit}>
                            Edit
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="secondary">
                                    <MdOutlineMoreVert className="h-5 w-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={fetchMonitorDetails} className="cursor-pointer">Reload Data</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setIsRenameDialogOpen(true)} className="cursor-pointer">Rename Monitor</DropdownMenuItem>
                                <DropdownMenuItem onClick={handleRunOnce} className="cursor-pointer">Run Once</DropdownMenuItem>
                                <DropdownMenuItem onClick={handleDelete} className="cursor-pointer text-red-600">Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {/* Rename Dialog */}
                <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Rename Monitor</DialogTitle>
                        </DialogHeader>
                        <div className="my-4">
                            <Input
                                value={newMonitorName}
                                onChange={(e) => setNewMonitorName(e.target.value)}
                                placeholder="Enter new monitor name"
                            />
                        </div>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button variant="outline">Cancel</Button>
                            </DialogClose>
                            <Button onClick={handleRename}>
                                Rename
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <motion.div 
                    variants={fadeInVariants}
                    transition={{ delay: 0.2 }}
                    className="space-y-4"
                >
                    {/* Top row with 3 cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card>
                            <CardContent className="p-6 min-h-40 flex flex-col justify-between">
                                <div>
                                    <h3 className="text-sm font-bold text-primary mb-2">Current Status</h3>
                                    {monitor.lastRun?.success ? (
                                        <p className="text-2xl font-bold text-violet-500">Success</p>  
                                    ) : (
                                        <p className="text-2xl font-bold text-red-500">Down</p>
                                    )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {monitor.successfulRunStreak > 0 
                                        ? `${monitor.successfulRunStreak} successful ${monitor.successfulRunStreak === 1 ? 'run' : 'runs'} in a row`
                                        : 'No successful runs yet'
                                    }
                                </p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent 
                                className="p-6 min-h-40 flex flex-col justify-between cursor-pointer hover:bg-accent/50 transition-colors"
                                onClick={() => monitor.lastRun?.runId && navigate(`/monitors/${id}/runs/${monitor.lastRun.runId}`)}
                            >
                                <div>
                                    <h3 className="text-sm font-bold text-primary mb-2">Last Run</h3>
                                    <p className="text-2xl font-bold text-violet-500">
                                        {formatResponseTime(monitor.lastRun?.result?.responseTime, true)}
                                    </p>
                                </div>
                                <p className="text-sm text-muted-foreground">{monitor.lastRun?.startTime || 'No runs yet'}</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-6 min-h-40 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-bold text-primary">Bot Insights™</h3>
                                        <p className="text-sm font-bold text-primary">
                                            {formatResponseTime(monitor.stats?.last24h?.averageResponseTime)}
                                        </p>
                                    </div>
                                    <InsightGrid insights={monitor.botInsights?.data} />
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {monitor.botInsights?.statusSummary || "Analyzing monitor data..."}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Bottom full-width card with 4 sections */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex divide-x">
                                <StatSection 
                                    title="Last 24 Hours" 
                                    value={monitor.stats?.last24h?.uptime || '0%'}
                                    description={`${monitor.stats?.last24h?.successCount || 0} successful runs`}
                                />
                                <StatSection 
                                    title="Last 7 Days" 
                                    value={monitor.stats?.last7d?.uptime || '0%'}
                                    description={`${monitor.stats?.last7d?.successCount || 0} successful runs`}
                                />
                                <StatSection 
                                    title="Last 30 Days" 
                                    value={monitor.stats?.last30d?.uptime || '0%'}
                                    description={`${monitor.stats?.last30d?.successCount || 0} successful runs`}
                                />
                                <StatSection 
                                    title="Last 365 Days" 
                                    value={monitor.stats?.last365d?.uptime || '0%'}
                                    description={`${monitor.stats?.last365d?.successCount || 0} successful runs`}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                <motion.div
                    variants={fadeInVariants}
                    transition={{ delay: 0.6 }}
                >
                    <Card>
                        <CardHeader>
                            <CardTitle>
                                {monitor.actionType === 'ping' ? 'Response Time' : 'Run Duration'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="h-[300px]">
                            <ResponseTimeChart data={monitor.chartData} />
                        </CardContent>
                    </Card>
                </motion.div>

                <motion.div
                    variants={fadeInVariants}
                    transition={{ delay: 0.6 }}
                >
                    <Card>
                        <CardHeader>
                            <CardTitle>Recent Runs</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {monitor.recentRuns && monitor.recentRuns.length > 0 ? (    
                                    monitor.recentRuns.map((run) => (
                                        <div 
                                            key={run.id} 
                                            className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors cursor-pointer"
                                            onClick={() => handleRunClick(run)}
                                        >
                                            <div className="flex items-center space-x-4">
                                                <div className="relative">
                                                    <div 
                                                        className={`
                                                            absolute 
                                                            inset-0 
                                                            rounded-full 
                                                            ${run.success ? 'bg-emerald-400/40' : 'bg-red-500/40'} 
                                                            w-4 h-4
                                                        `} 
                                                    />
                                                    <div 
                                                        className={`
                                                            relative
                                                            w-2 h-2 
                                                            rounded-full 
                                                            ${run.success ? 'bg-emerald-400' : 'bg-red-500'}
                                                            m-1
                                                        `} 
                                                    />
                                                </div>
                                                <div>
                                                    <div className="flex items-center space-x-2">
                                                        <Badge 
                                                            variant={run.success ? 'secondary' : 'destructive'}
                                                            className="font-medium"
                                                        >
                                                            {run.success ? 'Success' : 'Failed'}
                                                        </Badge>
                                                        <span className="text-sm text-muted-foreground">
                                                            {run.startTime}
                                                        </span>
                                                    </div>
                                                    {!run.success && run.note && (
                                                        <p className="text-sm text-red-500 mt-1">
                                                            {run.note}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <FiClock className="text-muted-foreground" />
                                                <span className="font-medium">
                                                    {formatResponseTime(run.result?.responseTime)}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-8 text-center">
                                        <FiActivity className="w-12 h-12 text-muted-foreground mb-3" />
                                        <p className="text-muted-foreground">No runs recorded yet</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure you want to delete this monitor?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This action cannot be undone. This will permanently delete the monitor
                                and remove all associated data.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="my-6">
                            <p className="text-sm text-muted-foreground mb-3">
                                To confirm, type <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-mono text-[14px]">{monitor?.name}</span> below:
                            </p>
                            <Input
                                value={deleteConfirmation}
                                onChange={(e) => setDeleteConfirmation(e.target.value)}
                                placeholder={monitor?.name}
                                className="mt-2"
                            />
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={confirmDelete}
                                disabled={deleteConfirmation.toLowerCase() !== monitor?.name.toLowerCase()}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                <RunDetailsDialog
                    monitorId={id}
                    runId={runId}
                    open={!!runId}
                    onOpenChange={(open) => {
                        if (!open) {
                            navigate(`/monitors/${id}`);
                        }
                    }}
                />
            </motion.div>
        </Layout>
    );
};

const LoadingState = () => (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center space-x-4 animate-pulse">
        <AiOutlineLoading3Quarters className="text-2xl text-primary animate-spin" />
        <h1 className="text-xl font-semibold">Crunching the data just for you</h1>
    </div>
);

export default MonitorDetails;